import { PriceBucket, MarketCapBucket, GapPercentBucket, VolumeBucket, BestSetup, BucketAnalysisData } from '../types';

export const applyBiasToMetrics = <T extends { redCloseRate?: number; successRate?: number; redClosePercentage?: number; greenCloses?: number; redCloses?: number }>(
  data: T,
  bias: 'long' | 'short'
): T => {
  if (bias === 'long') {
    return data;
  }

  const inverted = { ...data };

  if ('successRate' in inverted && typeof inverted.successRate === 'number') {
    inverted.successRate = 100 - inverted.successRate;
  }

  if ('redCloseRate' in inverted && typeof inverted.redCloseRate === 'number') {
    inverted.redCloseRate = 100 - inverted.redCloseRate;
  }

  if ('redClosePercentage' in inverted && typeof inverted.redClosePercentage === 'number') {
    inverted.redClosePercentage = 100 - inverted.redClosePercentage;
  }

  if ('greenCloses' in inverted && 'redCloses' in inverted) {
    const temp = inverted.greenCloses;
    inverted.greenCloses = inverted.redCloses;
    inverted.redCloses = temp;
  }

  return inverted;
};

export const applyBiasToPriceBuckets = (buckets: PriceBucket[], bias: 'long' | 'short'): PriceBucket[] => {
  if (bias === 'long') return buckets;

  return buckets.map(bucket => ({
    ...bucket,
    successRate: 100 - bucket.successRate,
    redCloseRate: 100 - bucket.redCloseRate
  }));
};

export const applyBiasToMarketCapBuckets = (buckets: MarketCapBucket[], bias: 'long' | 'short'): MarketCapBucket[] => {
  if (bias === 'long') return buckets;

  return buckets.map(bucket => ({
    ...bucket,
    successRate: 100 - bucket.successRate,
    redCloseRate: 100 - bucket.redCloseRate
  }));
};

export const applyBiasToGapPercentBuckets = (buckets: GapPercentBucket[], bias: 'long' | 'short'): GapPercentBucket[] => {
  if (bias === 'long') return buckets;

  return buckets.map(bucket => ({
    ...bucket,
    redCloseRate: 100 - bucket.redCloseRate
  }));
};

export const applyBiasToVolumeBuckets = (buckets: VolumeBucket[], bias: 'long' | 'short'): VolumeBucket[] => {
  if (bias === 'long') return buckets;

  return buckets.map(bucket => ({
    ...bucket,
    successProbability: 100 - bucket.successProbability
  }));
};

export const applyBiasToBucketAnalysis = (analysis: BucketAnalysisData[], bias: 'long' | 'short'): BucketAnalysisData[] => {
  if (bias === 'long') return analysis;

  return analysis.map(item => ({
    ...item,
    redClosePercentage: 100 - item.redClosePercentage,
    greenCloses: item.redCloses,
    redCloses: item.greenCloses
  }));
};

export const applyBiasToBestSetups = (setups: BestSetup[], bias: 'long' | 'short'): BestSetup[] => {
  if (bias === 'long') return setups;

  return setups.map(setup => {
    const invertedSuccessRate = 100 - setup.successRate;

    const biasType = setup.parameters['Bias Trading'];
    const invertedBiasType = biasType === 'LONG' ? 'SHORT' : biasType === 'SHORT' ? 'LONG' : biasType;

    const newTitle = setup.title === 'Long Bias' ? 'Short Bias' :
                     setup.title === 'Short Bias' ? 'Long Bias' : setup.title;

    const newDescription = bias === 'short'
      ? setup.description.replace(/long/gi, 'SHORT_TEMP').replace(/short/gi, 'LONG_TEMP').replace(/SHORT_TEMP/gi, 'short').replace(/LONG_TEMP/gi, 'long')
      : setup.description;

    return {
      ...setup,
      title: newTitle,
      description: newDescription,
      successRate: invertedSuccessRate,
      parameters: {
        ...setup.parameters,
        'Tasso Successo': `${invertedSuccessRate.toFixed(1)}%`,
        'Tasso Chiusure Rosse': `${(100 - parseFloat(setup.parameters['Tasso Chiusure Rosse'])).toFixed(1)}%`,
        'Bias Trading': invertedBiasType
      },
      chartData: [
        { name: 'Successo', value: invertedSuccessRate },
        { name: 'Fallimento', value: 100 - invertedSuccessRate }
      ]
    };
  });
};

export const getBiasColor = (bias: 'long' | 'short', isPositive: boolean): string => {
  if (bias === 'long') {
    return isPositive ? 'text-green-400' : 'text-red-400';
  } else {
    return isPositive ? 'text-red-400' : 'text-green-400';
  }
};

export const getBiasBgColor = (bias: 'long' | 'short', isPositive: boolean): string => {
  if (bias === 'long') {
    return isPositive ? 'bg-green-500/10' : 'bg-red-500/10';
  } else {
    return isPositive ? 'bg-red-500/10' : 'bg-green-500/10';
  }
};

export const getBiasBorderColor = (bias: 'long' | 'short', isPositive: boolean): string => {
  if (bias === 'long') {
    return isPositive ? 'border-green-500/30' : 'border-red-500/30';
  } else {
    return isPositive ? 'border-red-500/30' : 'border-green-500/30';
  }
};

export const getSuccessRateLabel = (bias: 'long' | 'short'): string => {
  return bias === 'long' ? 'Tasso di Successo (Chiusure Verdi)' : 'Tasso di Successo (Chiusure Rosse)';
};

export const getRedCloseLabel = (bias: 'long' | 'short'): string => {
  return bias === 'long' ? '% Chiusure Rosse' : '% Chiusure Verdi';
};
